import { useState } from 'react';
import clsx from 'clsx';
import { safeHttpUrl } from '@/lib/safeUrl';
import type { LinkPreview } from '@flock-types/chat.types';

interface Props {
  preview: LinkPreview;
  own: boolean;
}

/**
 * YouTube card matching mobile's YouTubeEmbedCard: thumbnail with a red play
 * button + title/"YouTube" footer; clicking swaps in the player (lite-embed —
 * no iframe cost until the member actually plays).
 */
export default function YouTubeEmbedCard({ preview, own }: Props) {
  const [playing, setPlaying] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);

  // thumbnailUrl first (hqdefault — always exists); image is often
  // maxresdefault which 404s for many videos. Same order as mobile.
  const imageUrl = safeHttpUrl(preview.thumbnailUrl) || safeHttpUrl(preview.image);
  const safePreviewUrl = safeHttpUrl(preview.url);
  const safeEmbedUrl = safeHttpUrl(preview.embedUrl);
  const embedSrc = safeEmbedUrl
    ? `${safeEmbedUrl}${safeEmbedUrl.includes('?') ? '&' : '?'}autoplay=1`
    : preview.providerId
      ? `https://www.youtube-nocookie.com/embed/${preview.providerId}?autoplay=1&playsinline=1&rel=0`
      : null;

  return (
    <div
      className={clsx(
        'mt-2 w-80 max-w-full overflow-hidden rounded-xl border',
        own ? 'border-white/20 bg-white/10' : 'border-flock-navy-200 bg-flock-navy-50'
      )}
    >
      {playing && embedSrc ? (
        <div className="aspect-video w-full bg-black">
          <iframe
            src={embedSrc}
            title={preview.title || 'YouTube video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="h-full w-full border-0"
          />
        </div>
      ) : (
        <button
          onClick={() => {
            if (embedSrc) {
              setPlaying(true);
            } else if (safePreviewUrl) {
              window.open(safePreviewUrl, '_blank', 'noopener');
            }
          }}
          className="relative block aspect-video w-full bg-black"
          aria-label="Play YouTube video"
        >
          {imageUrl && !thumbFailed && (
            <img
              src={imageUrl}
              alt=""
              onError={() => setThumbFailed(true)}
              className="h-full w-full object-cover"
            />
          )}
          <span className="absolute inset-0 flex items-center justify-center bg-black/20 transition hover:bg-black/30">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600 shadow-lg">
              <svg viewBox="0 0 24 24" fill="white" className="ml-0.5 h-6 w-6" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </span>
        </button>
      )}

      {safePreviewUrl ? (
        <a
          href={safePreviewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block p-3 hover:bg-black/5"
        >
          <div
            className={clsx(
              'line-clamp-2 text-sm font-semibold',
              own ? 'text-white' : 'text-flock-navy-900'
            )}
          >
            {preview.title || 'YouTube video'}
          </div>
          <div className={clsx('mt-1 text-xs', own ? 'text-white/70' : 'text-flock-navy-500')}>
            YouTube
          </div>
        </a>
      ) : (
        <div className="block p-3">
          <div
            className={clsx(
              'line-clamp-2 text-sm font-semibold',
              own ? 'text-white' : 'text-flock-navy-900'
            )}
          >
            {preview.title || 'YouTube video'}
          </div>
          <div className={clsx('mt-1 text-xs', own ? 'text-white/70' : 'text-flock-navy-500')}>
            YouTube
          </div>
        </div>
      )}
    </div>
  );
}
